import PageLoader from "components/PageLoader"
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto.ts"
import { typedEnv } from "../../config/typedEnv"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Dialog,
  DialogBody,
  DialogFooter,
  Flex,
  LinkButton,
  SingleSelect,
  SingleSelectOption,
  Tab,
  TabGroup,
  Table,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  TextInput,
  Th,
  Thead,
  Tr,
  Typography,
} from "@strapi/design-system"
import { Check, Download, Eye, Plus, Upload } from "@strapi/icons"
import allLangs from "./allLangs.ts"
import { useAlert } from "providers/AlertProvider"

interface _TranslatesPageProps {
  onAlert: (data: { error: boolean; text: string }) => void
}

const AddLangDialog = ({ onClose, onAddLang, isOpen, langList }) => {
  const [lang, setLang] = useState(null)
  const [code, setCode] = useState(null)
  const { t } = useTranslation()

  return (
    <Dialog onClose={onClose} title={t("translatesPage.addNewLang")} isOpen={isOpen}>
      <DialogBody>
        <SingleSelect
          label={t("translatesPage.languagesLabel")}
          required
          hint={t("translatesPage.languagesHint")}
          onChange={(value) => {
            setCode(value)
            setLang(langList.find(({ code }) => code === value).lang)
          }}
          value={code}
        >
          {langList.map(({ lang, code }) => (
            <SingleSelectOption key={code} value={code}>{`${lang
              .slice(0, 1)
              .toUpperCase()}${lang.slice(1)}`}</SingleSelectOption>
          ))}
        </SingleSelect>
      </DialogBody>
      <DialogFooter
        startAction={
          <Button onClick={onClose} variant="tertiary">
            {t("common.cancel")}
          </Button>
        }
        endAction={
          <Button
            startIcon={<Check />}
            onClick={() => onAddLang({ lang, code })}
            disabled={!lang || !code}
          >
            {t("common.add")}
          </Button>
        }
      />
    </Dialog>
  )
}

const AddStringDialog = ({ onClose, onAddString, isOpen }) => {
  const [key, setKey] = useState("")
  const { t } = useTranslation()

  return (
    <Dialog onClose={onClose} title={t("translatesPage.addNewString")} isOpen={isOpen}>
      <DialogBody>
        <TextInput
          name="key"
          label={t("translatesPage.stringKeyLabel")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
          hint={t("translatesPage.stringKeyHint")}
          value={key}
        />
      </DialogBody>
      <DialogFooter
        startAction={
          <Button onClick={onClose} variant="tertiary">
            {t("common.cancel")}
          </Button>
        }
        endAction={
          <Button
            startIcon={<Check />}
            onClick={() => onAddString(key)}
            disabled={!key.length || key.length < 5}
          >
            {t("common.add")}
          </Button>
        }
      />
    </Dialog>
  )
}

const baseLang = "english"

export const TranslatesPage = () => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const service = getTintVTOService()
  const [loading, setLoading] = useState(false)
  const [isAddLangDialog, setAddLangDialog] = useState(false)
  const [isAddStringDialog, setAddStringDialog] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [langIndex, setLangIndex] = useState(0)
  const [data, setData] = useState<any[]>([])
  const langList = useMemo(
    () => Object.keys(data).sort((a, _b) => (a === baseLang ? -1 : 1)),
    [data]
  )

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      const { data } = await service.getTranslates()
      const newData = data.loadLangConfigs.reduce((acc, str) => {
        const parsed = JSON.parse(str)
        const langName = allLangs.find((item) => item.code === parsed.code).lang
        return {
          ...acc,
          [langName]: parsed,
        }
      }, {})
      setData(newData)
      setLoading(false)
    })()
  }, [])

  const onSave = useCallback(() => {
    ;(async () => {
      setSaveLoading(true)
      await service.saveTranslates(JSON.stringify(data))
      setSaveLoading(false)

      showAlert({
        error: false,
        text: t("translatesPage.saved"),
      })
    })()
  }, [showAlert, data, service, t])

  const hiddenFileInput = useRef(null)

  const onExport = useCallback(() => {
    setExportLoading(true)

    const rows = [
      [t("translatesPage.stringColumn"), ...langList],
      ...Object.keys(data[baseLang].list).map((key) => [
        key,
        ...langList.map((lang) => data[lang].list[key]),
      ]),
    ]

    const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n")

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", "translates.csv")
    document.body.appendChild(link)

    link.click()
    setExportLoading(false)
  }, [data, langList, t])

  const onImport = useCallback(
    (e) => {
      setImportLoading(true)
      const file = (e.target as HTMLInputElement).files[0]
      const reader = new FileReader()

      reader.readAsText(file)

      reader.onload = () => {
        const rows = reader.result
          .toString()
          .split("\n")
          .map((line) => line.split(","))
        const newLangList = rows[0].slice(1)
        const lines = rows.slice(1).reduce(
          (acc, line) => ({
            ...acc,
            [line[0]]: line.slice(1),
          }),
          {}
        )
        const linesKeys = Object.keys(lines)
        const newData = newLangList.reduce(
          (acc, lang, index) => ({
            ...acc,
            [lang]: {
              code: allLangs.find((item) => item.lang === lang).code,
              list: linesKeys.reduce(
                (acc, item) => ({
                  ...acc,
                  [item]: lines[item][index],
                }),
                {}
              ),
            },
          }),
          {}
        )
        setData(newData as [])
        setImportLoading(false)

        showAlert({
          error: false,
          text: t("translatesPage.imported"),
        })
      }

      reader.onerror = () => {
        console.error(reader.error)
        setImportLoading(false)
        showAlert({
          error: true,
          text: `${t("translatesPage.importError")}: ${reader.error}`,
        })
      }
    },
    [showAlert, t]
  )

  const onAddLang = useCallback(
    ({ lang, code }) => {
      setData({
        ...data,
        [lang]: {
          code,
          list: Object.keys(data[baseLang].list).reduce(
            (acc, key) => ({
              ...acc,
              [key]: "",
            }),
            {}
          ),
        },
      })
      setAddLangDialog(false)
    },
    [data]
  )

  const onAddString = useCallback(
    (key) => {
      langList.forEach((lang) => (data[lang].list[key] = ""))
      setData({ ...data })
      setAddStringDialog(false)
    },
    [data]
  )

  const onTranslateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const lang = e.target.getAttribute("data-lang")
      const listItem = e.target.getAttribute("data-item")

      data[lang].list[listItem] = e.target.value
      setData({ ...data })
    },
    [data]
  )

  return (
    <Box>
      <AddLangDialog
        isOpen={isAddLangDialog}
        onAddLang={onAddLang}
        onClose={() => setAddLangDialog(false)}
        langList={allLangs.filter(({ lang }) => !langList.includes(lang))}
      />
      <AddStringDialog
        isOpen={isAddStringDialog}
        onAddString={onAddString}
        onClose={() => setAddStringDialog(false)}
      />
      <BaseHeaderLayout
        title={t("translatesPage.title")}
        subtitle={t("translatesPage.subtitle")}
        as={"h1"}
        primaryAction={
          <Flex gap={2}>
            <Button
              variant="tertiary"
              startIcon={<Plus />}
              onClick={() => setAddStringDialog(true)}
            >
              {t("translatesPage.addString")}
            </Button>
            <Button variant="tertiary" startIcon={<Plus />} onClick={() => setAddLangDialog(true)}>
              {t("translatesPage.addLang")}
            </Button>
            <Button
              variant="tertiary"
              startIcon={<Download />}
              loading={exportLoading}
              onClick={onExport}
            >
              {t("translatesPage.exportCsv")}
            </Button>
            <Button
              variant="tertiary"
              startIcon={<Upload />}
              loading={importLoading}
              onClick={() => hiddenFileInput.current?.click()}
            >
              <input
                value=""
                ref={hiddenFileInput}
                onChange={onImport}
                accept="data:text/csv"
                type="file"
                hidden
              />
              {t("translatesPage.importCsv")}
            </Button>
            <LinkButton
              startIcon={<Eye />}
              variant="tertiary"
              href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/`}
            >
              {t("translatesPage.preview")}
            </LinkButton>
            <Button startIcon={<Check />} loading={saveLoading} onClick={onSave}>
              {t("translatesPage.save")}
            </Button>
          </Flex>
        }
      />
      <ContentLayout>
        {loading ? (
          <PageLoader />
        ) : (
          <TabGroup
            label={t("translatesPage.langTabs")}
            id="tabs"
            onTabChange={(selected) => setLangIndex(selected)}
            variant="simple"
            initialSelectedTabIndex={langIndex}
          >
            <Tabs>
              {langList.map((lang) => (
                <Tab hasError={false} key={lang}>
                  {lang}
                </Tab>
              ))}
            </Tabs>
            <TabPanels>
              {langList.map((lang) => (
                <Box paddingTop={4} key={lang}>
                  <TabPanel color="neutral800" padding={4} background="neutral0">
                    <Table colCount={2} rowCount={Object.keys(data[lang].list).length}>
                      <Thead>
                        <Tr>
                          <Th>
                            <Typography variant="sigma">
                              {t("translatesPage.stringKeyColumn")}
                            </Typography>
                          </Th>
                          <Th>
                            <Typography variant="sigma">
                              {t("translatesPage.stringValueColumn")}
                            </Typography>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Object.keys(data[lang].list).map((listItem) => (
                          <Tr key={`${lang}-${listItem}`}>
                            <Td>
                              <Typography variant="omega">{listItem}</Typography>
                            </Td>
                            <Td>
                              <TextInput
                                name="value"
                                aria-label={t("translatesPage.value")}
                                data-lang={lang}
                                data-item={listItem}
                                onChange={onTranslateChange}
                                value={data[lang].list[listItem]}
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TabPanel>
                </Box>
              ))}
            </TabPanels>
          </TabGroup>
        )}
      </ContentLayout>
    </Box>
  )
}
